import { useContext, useEffect, useMemo, useRef } from 'react';
import { FormikContext, FormikHelpers, FormikValues } from 'formik';

import { FormRefsContext, FormRulesContext, GetRuleNameContext, GetRuleRegexErrorContext, OnSubmitRefContext, TranslateAndReplaceParams, TranslationContext } from '../context';
import { RangeRule } from '../types';

import { useAlert } from './alert-hooks';
import { get as _get } from 'lodash';

export const useForm = () => useContext(FormikContext)!;
export const useFormRules = () => useContext(FormRulesContext)!;
export const useFormRefs = () => useContext(FormRefsContext)!;
export const useOnSubmitRef = () => useContext(OnSubmitRefContext)!;
export const useGetRuleName = () => useContext(GetRuleNameContext)!;
export const useGetRuleRegexError = () => useContext(GetRuleRegexErrorContext)!;
export const useGTranslation = () => useContext(TranslationContext)!.useTranslate();
export const useGLanguage = () => useContext(TranslationContext)?.useLanguage() || 'he';
export const useGDirection = () => useContext(TranslationContext)?.useDirection() || 'rtl';
export const useTranslateAndReplace = () => useContext(TranslationContext)!.translateAndReplace;
export const useGi18n = () => useContext(TranslationContext)!.usei18n;



export const useFieldProps = (field: string) => {
	const form = useForm();
	return useMemo(() => form.getFieldProps(field), [form, field]);
}

export const useTextFieldError = (field: string): string | undefined | null => {
	const form = useForm();
	const formRules = useFormRules();
	const translateAndReplace = useTranslateAndReplace();

	const smallerThan = useMemo(() => {
		return (formRules[field] as RangeRule)?.smallerThan ?? 0;
	}, [formRules, field]);

	const listenTo = useMemo(() => field.split(/[\[.]/)[0], [field])

	const fieldError = useMemo(() => {
		if (_get(form.touched, field) || _get(form.values, field)?.length > smallerThan) {
			let err = _get(form.errors, field);
			if (typeof err === 'string') return err;
			return translateAndReplace(err as TranslateAndReplaceParams ?? { path: '', values: [] });
		}
		else return null;
	}, [field, form.touched[listenTo], form.errors[listenTo], smallerThan, translateAndReplace]);

	return fieldError;
}

export const useCheckBoxError = (field: string): boolean => {
	const form = useForm();
	const translateAndReplace = useTranslateAndReplace();

	const checkBoxError = useMemo(() => Boolean(_get(form.touched, field) && translateAndReplace(_get(form.errors, field) as TranslateAndReplaceParams)),
		[form, field, translateAndReplace]);

	return checkBoxError;
}

export const useFileError = (field: string): string | undefined | null => {
	const form = useForm();
	const translateAndReplace = useTranslateAndReplace();

	const fieldError = useMemo(() => {
		return _get(form.touched, field) ? translateAndReplace(_get(form.errors, field) as TranslateAndReplaceParams) : null;
	}, [field, form.errors[field.split(/[\[.]/)[0]]]);

	return fieldError;
}


export const useUpdateOnSubmit = (submit: (values: FormikValues, formikHelpers: FormikHelpers<FormikValues>) => void | Promise<any>) => {
	const onSubmitRef = useOnSubmitRef();

	useEffect(() => {
		onSubmitRef.current = submit;
	}, [submit]);
}

export const useFormIsValidAlert = (invalidText?: string | null, callback: () => void = () => null) => {
	const isSubmittingRef = useRef(false);

	const form = useForm();
	const alert = useAlert();
	const translate = useGTranslation()
	const invalidInfoText = translate("error.invalidInfo");

	useEffect(() => {
		if (form.isSubmitting) isSubmittingRef.current = true;

		else if (isSubmittingRef.current === true) {
			isSubmittingRef.current = false;
			if (form.isValid) return;

			invalidText !== null && alert(invalidText || invalidInfoText);
			callback();
		}
	}, [form.isSubmitting]);
}